import { EventEmitter, Injectable, OnDestroy } from '@angular/core';
import { webSocket, WebSocketSubject } from 'rxjs/webSocket';
import { BehaviorSubject, Observable, Subject, Subscription, timer } from 'rxjs';
import { catchError, takeUntil } from 'rxjs/operators';
import { environment } from '@11za/environments/env.dev';
import { Router } from '@angular/router';
import { ApiManager } from '../../utilities/api-manager';
import { apiEndpoints } from '../../constants/api-endpoints';
import { ChatService } from '../chat/chat.service';
import { InitialService } from '../initial/initial.service';
import { swalHelper } from '../../constants/swal-helper';
import { AppStorage } from '../../utilities/app-storage';

@Injectable({
  providedIn: 'root'
})
export class WebSocketService implements OnDestroy {
  private static readonly RECONNECT_DELAY_MS = 3000;

  private socket$: WebSocketSubject<any> | null = null;
  private readonly messagesSubject = new Subject<any>();
  public readonly messages$ = this.messagesSubject.asObservable();

  private readonly isConnectedSubject = new BehaviorSubject<boolean>(false);
  public readonly isConnected$ = this.isConnectedSubject.asObservable();

  private messageQueue: any[] = [];
  private destroy$ = new Subject<void>();
  private notFirst = false;

  // Emitters for specific events
  public readonly imageHeaderUrl = new EventEmitter<any>();
  public readonly messageReceived = new EventEmitter<any>();
  public readonly loaderInChat: EventEmitter<number> = new EventEmitter<number>();
  public readonly customerExport = new EventEmitter<any>();
  public readonly logsExport = new EventEmitter<any>();
  public readonly markAsRead = new EventEmitter<any>();
  public readonly shopifyCustomersSync = new EventEmitter<any>();
  public readonly assign = new EventEmitter<any>();
  public readonly broadcastImport = new EventEmitter<any>();
  public readonly deleteCustomer = new EventEmitter<any>();
  public readonly deleteTag = new EventEmitter<any>();
  public readonly revertCustomer = new EventEmitter<any>();
  public readonly syncTemplate = new EventEmitter<any>();
  public readonly bulkRecreateTemplate = new EventEmitter<any>();
  public readonly messageIdReceived = new EventEmitter<any>();
  public readonly notificationReceived = new EventEmitter<any>();
  public readonly errorOccurred = new EventEmitter<any>();
  public readonly queueChatsCount = new EventEmitter<any>();
  public readonly customerAnalyticsExportProgress = new EventEmitter<any>();
  public readonly costCappingApplyProgress = new EventEmitter<any>();
  public readonly costCappingApply = new EventEmitter<any>();
  public readonly combainedExport = new EventEmitter<any>();
  public readonly templateExportEvent = new EventEmitter<any>();
  public readonly assignChatReceived = new EventEmitter<any>();
  public readonly newDropitOrder = new EventEmitter<any>();

  private loaderCount = 0;

  incrementLoaderInChat() {
    this.loaderCount += 1;
    this.loaderInChat.emit(this.loaderCount);
  }
  decrementLoaderInChat() {
    if (this.loaderCount > 0) {
      this.loaderCount -= 1;
      this.loaderInChat.emit(this.loaderCount);
    }
    // Optional: If you still want to emit even when count is 0 (to notify others)
    else {
      this.loaderInChat.emit(0);
    }
  }
  public readonly headerCountAndChat = new BehaviorSubject<{
    totalChates: number,
    yourChat: number,
    otherChat: number
  }>({
    totalChates: 0,
    yourChat: 0,
    otherChat: 0
  });

  public readonly headerCountAndChat$ = this.headerCountAndChat.asObservable();

  constructor(
    private router: Router,
    private apiManager: ApiManager,
    private chatService: ChatService,
    public initialService: InitialService,
    private storage: AppStorage
  ) {
    this.initialService.isOnline$
      .pipe(takeUntil(this.destroy$))
      .subscribe(status => {
        if (status && this.notFirst) {
          this.messageReceived.emit(null);
          this.assignChatReceived.emit(null);
          this.initializeWebSocket();
        } else {
          this.notFirst = true;
        }
      });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
    this.closeConnection();
  }

  public initializeWebSocket(): void {
    if (this.socket$ && !this.socket$.closed) {
      return;
    }

    this.socket$ = webSocket({
      url: environment.WebSocketUrl,
      openObserver: {
        next: () => {
          this.isConnectedSubject.next(true);
          this.flushMessageQueue();
        },
      },
      closeObserver: {
        next: () => {
          this.isConnectedSubject.next(false);
          this.scheduleReconnection();
        },
      },
    });

    this.setupSocketHandlers();
  }

  private setupSocketHandlers(): void {
    this.socket$?.pipe(
      catchError(error => {
        console.error('WebSocket error:', error);
        this.errorOccurred.emit(error);
        return [];
      }),
      takeUntil(this.destroy$)
    ).subscribe({
      next: (message) => this.handleIncomingMessage(message),
      error: (error) => console.error('Subscription error:', error),
      complete: () => console.warn('Connection completed')
    });
  }

  private reconnectScheduled = false;
  private scheduleReconnection(): void {
    if (this.reconnectScheduled) {
      return;
    }
    this.reconnectScheduled = true;
    timer(WebSocketService.RECONNECT_DELAY_MS)
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => {
        this.reconnectScheduled = false;
        this.initializeWebSocket();
      });
  }

  public sendMessage(message: any): void {
    if (this.isConnectedSubject.getValue() && this.socket$) {
      this.socket$.next(message);
    } else {
      console.warn('Queuing message - not connected');
      this.messageQueue.push(message);
      if (!this.socket$ || this.socket$.closed) {
        this.initializeWebSocket();
      }
    }
  }

  private flushMessageQueue(): void {
    while (this.messageQueue.length > 0) {
      const message = this.messageQueue.shift();
      this.socket$?.next(message);
    }
  }

  public closeConnection(): void {
    if (this.socket$) {
      this.socket$.complete();
      this.socket$ = null;
    }
    this.isConnectedSubject.next(false);
  }

  newChatMessageObj: any = null
  async parentUnseenCounterChatNew(): Promise<void> {
    try {
      if (this.newChatMessageObj && this.newChatMessageObj?.messageId && this.newChatMessageObj?.chatMessage?.type !== 'queueChat') {
        const totalCount = this.headerCountAndChat.getValue()
        totalCount.totalChates = totalCount.totalChates + 1
        this.headerCountAndChat.next({
          ...this.headerCountAndChat.getValue(),
          ...totalCount
        });
        this.newChatMessageObj = null
      } else {
        const result = await this.chatService.getChatParentUnseenCounter();
        if (result.Data) {
          const totalChates = result.Data.myChats + result.Data.otherChats + result.Data.teams;
          this.headerCountAndChat.next({
            ...this.headerCountAndChat.getValue(),
            totalChates
          });
        }
      }
    } catch (error) {
      console.error('Failed to update chat counter:', error);
    }
  }

  private handleIncomingMessage(message: any): void {
    if (!message?.event) {
      console.warn('Received malformed message:', message);
      return;
    }

    switch (message.event) {
      case 'videoUrlUpdate':
        this.imageHeaderUrl.emit(message.data);
        break;

      case 'newMessage':
        this.messageReceived.emit(message.data);
        this.newChatMessageObj = message.data
        this.parentUnseenCounterChatNew();
        if (message.data?.chatMessage?.type === 'queueChat') {
          this.handleQueueCount();
        }
        break;

      case 'messageStatusUpdated':
        this.notificationReceived.emit(message.data);
        break;
      case 'customerAnalyticsExportProgress':
        this.customerAnalyticsExportProgress.emit(message.data);
        break;
      case 'costCappingApplyProgress':
        this.costCappingApplyProgress.emit(message.data);
        break;
      case 'costCappingApply':
        this.costCappingApply.emit(message.data);
        break;
      case 'customerExport':
        if (message?.data?.error) {
          // Also push the failure to the % emitter so the progress bar (stuck otherwise) resets.
          this.customerExport.emit({ error: true, message: message.data.message });
        }
        this.handleExportMessage(message);
        break;
      case 'broadcastExport':
      case 'logsExport':
      case 'markAsRead':
      case 'shopifyCustomersSync':
      case 'assign':
      case 'broadcastImport':
      case 'deleteCustomer':
      case 'deleteTag':
      case 'revertCustomer':
      case 'syncTemplate':
        this.handleExportMessage(message);
        break;
      case 'productsExport':
      case 'ordersExport':
      case 'filteredBroadcastExport':
      case 'customerAnalyticsExport':
        this.handleExportMessage(message);
        break;
      case 'combainedExport':
        this.combainedExport.emit(message.data);
        break;
      case 'templateExport':
        this.templateExportEvent.emit(message.data);
        break;

      case 'onIncomingMessage':
        this.handleNotification(message);
        this.chatService.onIncommingMessage(message.data)
        break;

      case 'autoLogoutWeb':
        this.handleAutoLogout();
        break;
      case 'asssignChat':
        this.assignChatReceived.emit(message.data);
        this.handleQueueCount();
        if (message.data?.message) {
          swalHelper.showToast(message.data.message, 'info');
        }
        break;

      case 'newDropitOrder':
        this.newDropitOrder.emit(message.data);
        this.handleOrderNotification(message.data);
        break;

      case 'error':
        console.error('WebSocket error:', message.data);
        this.errorOccurred.emit(message.data);
        break;
      case 'customerExportProgress':
        this.customerExport.emit(message.data);
        break;
      case 'logsExportProgress':
        this.logsExport.emit(message.data);
        break;
      case 'markAsReadProgress':
        this.markAsRead.emit(message.data);
        break;
      case 'shopifyCustomersSyncProgress':
        this.shopifyCustomersSync.emit(message.data);
        break;
      case 'assignProgress':
        this.assign.emit(message.data);
        break;
      case 'broadcastImportProgress':
        this.broadcastImport.emit(message.data);
        break;
      case 'deleteCustomerProgress':
        this.deleteCustomer.emit(message.data);
        break;
      case 'deleteTagProgress':
        this.deleteTag.emit(message.data);
        break;
      case 'revertCustomerProgress':
        this.revertCustomer.emit(message.data);
        break;
      case 'syncTemplateProgress':
        this.syncTemplate.emit(message.data);
        break;
      case 'bulkRecreateTemplateProgress':
        this.bulkRecreateTemplate.emit(message.data);
        break;
      case 'reportGenerationProgress':
        this.templateExportEvent.emit(message.data);
        break;
      default:
        // console.warn('Unknown message type:', message);
        this.messagesSubject.next(message);
    }
  }

  private handleExportMessage(message: any): void {
    if (message?.data?.error) {
      swalHelper.error(message.data.message || 'Something went wrong while exporting');
      return;
    }
    const url = message?.data?.link || '';
    if (!url) return;
    const dedupKey = `export_download:${url}`;
    const now = Date.now();
    const claimedAt = Number(localStorage.getItem(dedupKey));
    if (claimedAt && now - claimedAt < 60_000) return;
    localStorage.setItem(dedupKey, String(now));
    window.open(url);
  }

  private handleNotification(message: any): void {
    if (!('Notification' in window)) {
      console.log('Browser does not support notifications');
      return;
    }

    Notification.requestPermission().then(permission => {
      if (permission !== 'granted') {
        console.log('Notifications denied');
        return;
      }

      const messageData = message.data;
      const senderName = messageData.customername || 'Unknown Sender';
      const messageText = messageData.content?.text || 'No message content';

      this.notificationReceived.emit(message.data);
      this.chatService.invokelodeUser();

      setTimeout(() => {
        new Notification(`New Message from ${this.initialService.appName} - ${senderName}`, {
          body: messageText,
          // icon: '/images/11zaR.svg'
          icon: this.initialService.partnerpanels?.favicon_icon || '/images/11zaR.svg'
        });
      }, 1000);
    });
  }

  private handleOrderNotification(data: any): void {
    if (!('Notification' in window)) return;
    Notification.requestPermission().then(permission => {
      if (permission !== 'granted') return;
      let title = 'New Order Received';
      let body = `${data?.customerName || 'Customer'} placed a new delivery order #${data?.invoiceNo || ''}`;
      if (data?.type === 'paymentSuccess') {
        title = 'Payment Confirmed';
        body = `${data?.customerName || 'Customer'} paid for order #${data?.invoiceNo || ''}`;
      } else if (data?.type === 'paymentFailed') {
        title = 'Payment Failed';
        body = `Payment for order #${data?.invoiceNo || ''} by ${data?.customerName || 'Customer'} was not completed`;
      } else if (data?.type === 'orderConfirm') {
        title = 'Order Confirmed by Customer';
        body = `${data?.customerName || 'Customer'} confirmed order #${data?.invoiceNo || ''}`;
      } else if (data?.type === 'orderCancel') {
        title = 'Order Cancelled by Customer';
        body = `${data?.customerName || 'Customer'} cancelled order #${data?.invoiceNo || ''}`;
      }
      new Notification(title, {
        body,
        icon: this.initialService.partnerpanels?.favicon_icon || '/images/11zaR.svg'
      });
    });
  }

  private handleAutoLogout(): void {
    this.storage.LogOutSession();
    this.router.navigateByUrl('/login').then(() => {
      window.location.reload();
    });
  }
  handleQueueCount(): void {
    this.queueChatsCount.emit();
  }
}